import classNames from "classnames";
import { formatDistance, format } from "date-fns";
import { useEffect, useState, useRef, useMemo } from "react";
import Modal from "react-modal";
import { useDispatch, useSelector } from "react-redux";
import ReactTooltip from "react-tooltip";

import { PWMessage, PWNotification } from "custom-models";

import useWebSocket from "shared/hooks/useWebSocket";
import { AuthState } from "shared/reducers/authReducer";
import { RequestStatusesState } from "shared/reducers/requestStatusesReducer";

import {
  createConversationMessage,
  fetchConversation,
  conversationMessageRecieved,
} from "./conversationActions";
import { ConversationsState } from "./conversationsReducer";

import "./ConversationModal.css";

const ConversationItem = ({
  item,
  currentUserId,
  nextItem,
}: {
  item: PWMessage | PWNotification;
  currentUserId?: number;
  nextItem?: PWMessage | PWNotification;
}) => {
  let showDate = true;
  let showUser = true;

  const topicMessage: PWMessage = item as PWMessage;
  const topicNotification = item as PWNotification;

  const nextPWMessage = nextItem as PWMessage;

  if (nextPWMessage && nextPWMessage.id && topicMessage && topicMessage.id) {
    if (nextPWMessage.author_id === topicMessage.author_id) showUser = false;
  }

  if (
    nextItem &&
    Date.parse(nextItem.created_at || "") - Date.parse(item?.created_at || "") <
      1000 * 60
  ) {
    showDate = false;
  }

  let createdAtStr;
  if (showDate) {
    const createdAtDate = new Date(Date.parse(item?.created_at || ""));
    let timestampFormat = "";
    if (createdAtDate.getDate() !== new Date().getDate())
      timestampFormat += "eeee, MMM d";
    if (createdAtDate.getFullYear() !== new Date().getFullYear())
      timestampFormat += " yyyy";
    if (timestampFormat) timestampFormat += " - ";
    createdAtStr = format(createdAtDate, `${timestampFormat} h:mm aa`);
  }

  if (topicMessage && topicMessage.id) {
    const mine = topicMessage.author_id === currentUserId;
    return (
      <div
        className={classNames("TopicMessage", { "TopicMessage--mine": mine })}
      >
        {!mine && showUser && (
          <div className="TopicMessage__user">#{topicMessage.author_id}</div>
        )}
        <div className="TopicMessage__body">{topicMessage.body}</div>
        {showDate && (
          <div className="TopicMessage__timestamp">{createdAtStr}</div>
        )}
      </div>
    );
  }

  if (topicNotification && topicNotification.uuid) {
    return (
      <div className="TopicNotification">
        <div className="TopicNotification__body">{topicNotification.body}</div>
        {showDate && (
          <div className="TopicNotification__timestamp">{createdAtStr}</div>
        )}
      </div>
    );
  }

  return null;
};

const ConversationModal = ({
  isOpen,
  topicType,
  topicId,
  closeModal,
}: {
  isOpen: boolean;
  topicType: string;
  topicId: number;
  closeModal: Function;
}) => {
  const dispatch = useDispatch() as Function;
  const fetchKey = fetchConversation.getRequestKey({ topicType, topicId });

  const isLoading = useSelector(
    ({ requestStatuses }: { requestStatuses: RequestStatusesState }) =>
      !!requestStatuses.loading[fetchKey]
  );
  // isLoadingRef is needed so that the ToolTip can be dynamically updated
  const isLoadingRef = useRef(isLoading);
  isLoadingRef.current = isLoading;

  const loadedAt = useSelector(
    ({ requestStatuses }: { requestStatuses: RequestStatusesState }) =>
      requestStatuses.loadedAt[fetchKey]
  );
  // loadedAtRef is needed so that the ToolTip can be dynamically updated
  const loadedAtRef = useRef(loadedAt);
  loadedAtRef.current = loadedAt;

  const createMessageKey = createConversationMessage.getRequestKey({
    topicType,
    topicId,
    body: "",
  });
  const isCreating = useSelector(
    ({ requestStatuses }: { requestStatuses: RequestStatusesState }) =>
      !!requestStatuses.loading[createMessageKey]
  );

  const conversation = useSelector(
    ({ conversations }: { conversations: ConversationsState }) => {
      const topicsByType = conversations[topicType];
      if (!topicsByType) return undefined;
      return topicsByType[topicId];
    }
  );

  const currentUserId = useSelector(
    ({ auth }: { auth: AuthState }) => auth?.user?.id
  );

  const messagesEndRef: any = useRef(null);
  const messageInputRef: any = useRef(null);

  const [newMessage, setNewMessage] = useState("");

  useWebSocket(
    isOpen &&
      !!topicType &&
      !!topicId &&
      `conversation/${topicType}/${topicId}`,
    {
      onMessageData: (data: any) =>
        dispatch(conversationMessageRecieved(topicType, topicId, data.message)),
    }
  );

  useEffect(() => {
    if (isOpen && !loadedAt && !isLoading) {
      dispatch(fetchConversation({ topicType, topicId }));
    }
  }, [isOpen, topicType, topicId]);

  const scrollToBottom = () => {
    messagesEndRef.current?.scrollIntoView({ behavior: "auto" });
  };

  useEffect(() => {
    if (!isCreating) {
      setNewMessage("");
      scrollToBottom();
      messageInputRef.current?.focus();
    }
  }, [isCreating, topicType, topicId]);

  useEffect(() => {
    if (isOpen && messagesEndRef.current) {
      scrollToBottom();
    }
  }, [
    conversation,
    conversation && Object.keys(conversation.messages)?.length,
    isOpen,
    messagesEndRef.current,
  ]);

  const onSubmitMessage = (ev: any) => {
    ev.preventDefault();
    dispatch(
      createConversationMessage({ topicType, topicId, body: newMessage })
    );
  };

  const onMouseDownResize = (ev: any) => {
    ev.preventDefault();
    alert("Do you have a problem with my size?");
  };

  const onMouseDownDrag = (ev: any) => {
    ev.preventDefault();
    alert("You can't drag me around!");
  };

  const onClickRefresh = (ev: any) => {
    ev.preventDefault();
    if (!isLoading) {
      dispatch(fetchConversation({ topicType, topicId }));
    }
  };

  const onClickClose = (ev: any) => {
    ev.preventDefault();
    closeModal();
  };

  const getTooltipContent = (dataTip: string) => {
    if (dataTip !== "CONVERSATION_REFRESH") return dataTip;

    if (isLoadingRef.current) return "Fetching...";

    if (!loadedAtRef.current) return false;

    return `Last fetched ${formatDistance(
      new Date(loadedAtRef.current),
      new Date()
    )} ago`;
  };

  const conversationItems: Array<PWMessage | PWNotification> = useMemo(() => {
    if (!conversation) return [];

    const items: Array<PWMessage | PWNotification> = [];
    return items
      .concat(Object.values(conversation.messages))
      .concat(Object.values(conversation.notifications))
      .sort(
        (a, b) =>
          Date.parse(a?.created_at || "") - Date.parse(b?.created_at || "")
      );
  }, [conversation]);

  return (
    <Modal
      isOpen={isOpen}
      className="ConversationModal"
      overlayClassName="ConversationOverlay"
      shouldCloseOnOverlayClick={false}
    >
      <header>
        <div className="ConversationModal__toolbar">
          <div
            className="ConversationModal__resize"
            onMouseDown={onMouseDownResize}
          >
            <div>&harr;</div>
          </div>
          <div
            className="ConversationModal__drag"
            onMouseDown={onMouseDownDrag}
          >
            &equiv;
          </div>
          <div
            className={classNames("ConversationModal__refresh", {
              loading: isLoading,
            })}
            onClick={onClickRefresh}
            data-tip="CONVERSATION_REFRESH"
          >
            <div>&#x21bb;</div>
          </div>
          <div className="ConversationModal__close" onClick={onClickClose}>
            &times;
          </div>
          <ReactTooltip getContent={[getTooltipContent, 1000]} />
        </div>
        <div className="ConversationModal__header">
          {topicType} #{topicId}
        </div>
      </header>
      <main className="ConversationModal__main">
        {conversationItems.map((item, i) => (
          <ConversationItem
            key={(item as PWMessage)?.id || (item as PWNotification)?.uuid}
            item={item}
            nextItem={conversationItems[i + 1]}
            currentUserId={currentUserId}
          />
        ))}
        <div ref={messagesEndRef} />
      </main>
      <footer className="ConversationModal__footer">
        <form
          className="ConversationModal__form"
          onSubmit={onSubmitMessage}
          autoComplete="off"
        >
          <input
            type="text"
            className="TopicMessage__input"
            value={newMessage}
            onChange={(ev) => setNewMessage(ev.currentTarget.value)}
            name="body"
            disabled={!loadedAt || isCreating}
            ref={messageInputRef}
            required
          />
          <input
            type="submit"
            value="Send"
            disabled={!loadedAt || isCreating}
          />
        </form>
      </footer>
    </Modal>
  );
};

export default ConversationModal;
